import React, { useState } from 'react';
import {
    Lock,
    Key,
    Shield,
    Eye,
    EyeOff,
    CheckCircle,
    AlertCircle,
    Save,
    X,
    ArrowLeft,
    Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './ChangePassword.css';
import { ApiUrl } from '../API/ApiUrl';
import Swal from 'sweetalert2';
import Toast from "../components/Toast/Toast";

const ChangePassword = () => {
    const navigate = useNavigate();
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');


    const [toast, setToast] = useState({
        show: false,
        message: "",
        type: "success"
    });
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        hasMinLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false,
        hasSpecialChar: false
    });
    const [saving, setSaving] = useState(false)

    const handleChangePassword = async (e) => {
        e.preventDefault();

        if (formData.newPassword !== formData.confirmPassword) {
            setToast({
                show: true,
                message: "New password and confirm password do not match",
                type: "error"
            });
            return;
        }

        if (formData.newPassword.length < 6) {
            setToast({
                show: true,
                message: "Password must be at least 6 characters",
                type: "error"
            });
            return;
        }

        setSaving(true)

        try {
            const token = localStorage.getItem("adminToken");

            const response = await fetch(`${ApiUrl}/change-password`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    current_password: formData.currentPassword,
                    new_password: formData.newPassword
                })
            });

            const data = await response.json();
            console.log("API response:", data);

            if (response.ok) {
                setToast({
                    show: true,
                    message: data.message || "Password updated successfully 🎉",
                    type: "success"
                })

                setTimeout(() => {
                    localStorage.removeItem('adminToken');
                    navigate('/admin/login');
                }, 2000)

                setFormData({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: ""
                });

            } else {
                // ❌ Error Toast
                setToast({
                    show: true,
                    message: data.message || "Failed to change password",
                    type: "error"
                });
            }

        } catch (error) {
            console.error(error);

            setToast({
                show: true,
                message: "Server error occurred",
                type: "error"
            });
        } finally {
            setSaving(false)
        }
    };


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        if (name === 'newPassword') {
            checkPasswordStrength(value);
        }
    };

    const checkPasswordStrength = (password) => {
        const strength = {
            score: 0,
            hasMinLength: password.length >= 8,
            hasUpperCase: /[A-Z]/.test(password),
            hasLowerCase: /[a-z]/.test(password),
            hasNumber: /[0-9]/.test(password),
            hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
        };

        // Calculate score
        let score = 0;
        if (strength.hasMinLength) score += 1;
        if (strength.hasUpperCase) score += 1;
        if (strength.hasLowerCase) score += 1;
        if (strength.hasNumber) score += 1;
        if (strength.hasSpecialChar) score += 1;

        strength.score = score;
        setPasswordStrength(strength);
    };

    const validateForm = () => {
        if (!formData.currentPassword) {
            setErrorMessage('Current password is required');
            return false;
        }

        if (!formData.newPassword) {
            setErrorMessage('New password is required');
            return false;
        }

        if (formData.newPassword.length < 8) {
            setErrorMessage('New password must be at least 8 characters long');
            return false;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setErrorMessage('New passwords do not match');
            return false;
        }

        // Check if new password is same as current
        if (formData.newPassword === formData.currentPassword) {
            setErrorMessage('New password must be different from current password');
            return false;
        }

        return true;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        // Simulate API call
        setTimeout(() => {
            setSuccessMessage('Password changed successfully!');
            setFormData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            setPasswordStrength({
                score: 0,
                hasMinLength: false,
                hasUpperCase: false,
                hasLowerCase: false,
                hasNumber: false,
                hasSpecialChar: false
            });

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);
        }, 1000);
    };

    const handleCancel = () => {
        setFormData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setPasswordStrength({
            score: 0,
            hasMinLength: false,
            hasUpperCase: false,
            hasLowerCase: false,
            hasNumber: false,
            hasSpecialChar: false
        });
        setErrorMessage('');
    };

    const getStrengthColor = () => {
        switch (passwordStrength.score) {
            case 0: return '#e2e8f0';
            case 1: return '#f56565';
            case 2: return '#ed8936';
            case 3: return '#ecc94b';
            case 4: return '#48bb78';
            case 5: return '#38a169';
            default: return '#e2e8f0';
        }
    };

    const getStrengthText = () => {
        switch (passwordStrength.score) {
            case 0: return 'Not entered';
            case 1: return 'Very Weak';
            case 2: return 'Weak';
            case 3: return 'Fair';
            case 4: return 'Good';
            case 5: return 'Strong';
            default: return '';
        }
    };

    const getStrengthWidth = () => {
        return `${(passwordStrength.score / 5) * 100}%`;
    };

    return (
        <div className="change-password-page">
            {/* Header */}
            <div className="page-header">
                <div className="header-left">
                    {/* <button className="back-btn" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </button> */}
                    <div className="title-section">
                        <div className="title-icon">
                            <Shield size={24} />
                        </div>
                        <div>
                            <h1 className="page-title">Change Password</h1>
                            <p className="page-subtitle">Update your password to keep your account secure</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Messages */}
            {successMessage && (
                <div className="alert success">
                    <CheckCircle size={18} />
                    <span>{successMessage}</span>
                </div>
            )}

            {errorMessage && (
                <div className="alert error">
                    <AlertCircle size={18} />
                    <span>{errorMessage}</span>
                </div>
            )}

            {/* Main Card */}
            <div className="password-card">
                <div className="password-card-header">
                    <Lock size={20} />
                    <h2>Password Update</h2>
                </div>

                <form onSubmit={handleChangePassword} className="password-form">
                    {/* Current Password */}
                    <div className="form-group">
                        <label>
                            <Key size={16} />
                            <span>Current Password <span className="required">*</span></span>
                        </label>
                        <div className="password-input-wrapper">
                            <input
                                type={showCurrentPassword ? "text" : "password"}
                                name="currentPassword"
                                value={formData.currentPassword}
                                onChange={handleInputChange}
                                placeholder="Enter your current password"
                                className="password-input"
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            >
                                {showCurrentPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* New Password */}
                    <div className="form-group">
                        <label>
                            <Lock size={16} />
                            <span>New Password <span className="required">*</span></span>
                        </label>
                        <div className="password-input-wrapper">
                            <input
                                type={showNewPassword ? "text" : "password"}
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleInputChange}
                                placeholder="Enter your new password"
                                className="password-input"
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                            >
                                {showNewPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                            </button>
                        </div>

                        {/* Password Strength Meter */}
                        {formData.newPassword && (
                            <div className="password-strength">
                                <div className="strength-bar">
                                    <div
                                        className="strength-fill"
                                        style={{
                                            width: getStrengthWidth(),
                                            backgroundColor: getStrengthColor()
                                        }}
                                    ></div>
                                </div>
                                <span className="strength-text" style={{ color: getStrengthColor() }}>
                                    {getStrengthText()}
                                </span>

                                <div className="strength-requirements">
                                    <div className={`requirement ${passwordStrength.hasMinLength ? 'met' : ''}`}>
                                        <span className="requirement-icon">
                                            {passwordStrength.hasMinLength ? '✓' : '○'}
                                        </span>
                                        <span>At least 8 characters</span>
                                    </div>
                                    <div className={`requirement ${passwordStrength.hasUpperCase ? 'met' : ''}`}>
                                        <span className="requirement-icon">
                                            {passwordStrength.hasUpperCase ? '✓' : '○'}
                                        </span>
                                        <span>Uppercase letter</span>
                                    </div>
                                    <div className={`requirement ${passwordStrength.hasLowerCase ? 'met' : ''}`}>
                                        <span className="requirement-icon">
                                            {passwordStrength.hasLowerCase ? '✓' : '○'}
                                        </span>
                                        <span>Lowercase letter</span>
                                    </div>
                                    <div className={`requirement ${passwordStrength.hasNumber ? 'met' : ''}`}>
                                        <span className="requirement-icon">
                                            {passwordStrength.hasNumber ? '✓' : '○'}
                                        </span>
                                        <span>Number</span>
                                    </div>
                                    <div className={`requirement ${passwordStrength.hasSpecialChar ? 'met' : ''}`}>
                                        <span className="requirement-icon">
                                            {passwordStrength.hasSpecialChar ? '✓' : '○'}
                                        </span>
                                        <span>Special character</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <div className="form-group">
                        <label>
                            <Lock size={16} />
                            <span>Confirm New Password <span className="required">*</span></span>
                        </label>
                        <div className="password-input-wrapper">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                placeholder="Confirm your new password"
                                className="password-input"
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                            </button>
                        </div>

                        {/* Password Match Indicator */}
                        {formData.confirmPassword && (
                            <div className="password-match">
                                {formData.newPassword === formData.confirmPassword ? (
                                    <span className="match-success">
                                        <CheckCircle size={14} />
                                        Passwords match
                                    </span>
                                ) : (
                                    <span className="match-error">
                                        <AlertCircle size={14} />
                                        Passwords do not match
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Security Tips */}
                    {/* <div className="security-tips">
            <h4>Password Security Tips:</h4>
            <ul>
              <li>Use a mix of uppercase and lowercase letters</li>
              <li>Include numbers and special characters</li>
              <li>Avoid common words or phrases</li>
              <li>Don't reuse passwords from other accounts</li>
              <li>Change your password regularly</li>
            </ul>
          </div> */}

                    {/* Form Actions */}
                    <div className="form-actions">
                        <button type="button" className="btn-cancel" onClick={handleCancel} disabled={saving}>
                            <X size={16} />
                            <span>Cancel</span>
                        </button>
                        <button type="submit" className="btn-save" disabled={saving}>
                            {/* <Save size={16} /> */}
                            {/* <span>Change Password</span> */}
                            {saving ? (
                                <>
                                    <Loader2 size={16} className="spin-icon" />
                                    <span>Changing...</span>
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    <span>Change Password</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast({ ...toast, show: false })}
                />
            )}
            {/* <div className="security-info">
        <div className="info-item">
          <Shield size={20} className="info-icon" />
          <div className="info-content">
            <h4>Two-Factor Authentication</h4>
            <p>Add an extra layer of security to your account</p>
            <button className="info-action">Enable 2FA</button>
          </div>
        </div>

        <div className="info-item">
          <Key size={20} className="info-icon" />
          <div className="info-content">
            <h4>Password Manager</h4>
            <p>Use a password manager to generate and store strong passwords</p>
            <a href="#" className="info-link">Learn more</a>
          </div>
        </div>

        <div className="info-item">
          <Lock size={20} className="info-icon" />
          <div className="info-content">
            <h4>Recent Activity</h4>
            <p>Last password change: 30 days ago</p>
            <p className="activity-note">Recommended to change every 90 days</p>
          </div>
        </div>
      </div> */}
        </div>
    );
};

export default ChangePassword;